import { _cloneDeep } from '@shared/aux_helper_functions';
import { GenieStatementResponse } from 'app/services/agents/databricks-genie-ai-chat.models';
import {
  DataBricksAiGenie_ChartAnalysisResult,
  DataBricksAiGenie_ChartAnalysisResult_ChartType,
} from './databricks-genie-ai-query-process-table.component';

export const isDateString = (value: string): boolean => {
  const datePatterns = [
    // /^\d{1,2}-\d{1,2}-\d{4}$/, // D-M-YYYY o DD-MM-YYYY
    /^\d{4}-\d{1,2}-\d{1,2}$/, // YYYY-M-D o YYYY-MM-DD
    /^\d{1,2}\/\d{1,2}\/\d{4}$/, // D/M/YYYY o DD/MM/YYYY
    /^\d{4}-\d{1,2}-\d{1,2}T\d{2}:\d{2}:\d{2}/, // ISO format con dígitos flexibles
    /^\d{13}$/, // Timestamp
  ];

  // Si coincide con algún patrón, verificar que sea una fecha válida
  if (datePatterns.some(pattern => pattern.test(value))) {
    const date = new Date(value);
    return !isNaN(date.getTime()); // Verificación adicional de fecha válida
  }

  return false;
};

export const determineChartType = (
  seriesData: Array<{ label: string; value: number }>
): DataBricksAiGenie_ChartAnalysisResult_ChartType => {
  // Verificar si las etiquetas son fechas
  const areLabelsDate = seriesData.every(item => {
    let rv = isDateString(item.label);
    return rv;
  });

  if (areLabelsDate) return 'time_line';

  // Resto de la lógica existente
  const recordCount = seriesData.length;
  const maxLabelLength = Math.max(...seriesData.map(item => item.label.length));

  if (recordCount <= 8) {
    return 'pie'; // Gráfico de torta para pocos registros
  } else if (maxLabelLength > 15 || recordCount > 12) {
    return 'horizontalBar'; // Barras horizontales para etiquetas largas o muchos registros
  } else {
    return 'bar'; // Barras verticales como caso por defecto
  }
};

export const isNumericColType = (colType: string): boolean => {
  const type = String(colType || '').toLowerCase();
  return type.includes('int') || type.includes('double') || type.includes('decimal') || type.includes('number');
};

export const getNumericColumns_OLD = (queryResponse: GenieStatementResponse, intensiveCheck = true): string[] => {
  const { manifest, result } = queryResponse.statement_response;

  return manifest.schema.columns
    .filter(col => {
      const colType = String(col.type_text || col.type_name).toLowerCase();

      const isNumericType =
        colType.includes('int') || colType.includes('double') || colType.includes('decimal') || colType.includes('number');

      if (!isNumericType) return false;

      if (!intensiveCheck) return true;

      // Si es un tipo numérico declarado, validar los valores
      const colIndex = manifest.schema.columns.findIndex(c => c.name === col.name);

      const columnValues = result.data_typed_array.map(row => {
        const value = row.values[colIndex];
        const numValue = value?.num !== undefined ? Number(value.num) : value?.str !== undefined ? Number(value.str) : NaN;
        return numValue;
      });

      const allNoNaN = columnValues.every(val => !isNaN(val));

      const someNonZero = columnValues.some(val => val !== 0);

      return allNoNaN && someNonZero && isNumericType;
    })
    .map(col => col.name);
};

export const getNumericColumns = (queryResponse: GenieStatementResponse, intensiveCheck = true): string[] => {
  const { manifest, result } = queryResponse.statement_response;

  return manifest.schema.columns
    .filter(col => {
      const colType = String(col.type_text || col.type_name).toLowerCase();

      const isNumericType =
        colType.includes('int') || colType.includes('double') || colType.includes('decimal') || colType.includes('number');

      if (!intensiveCheck) return isNumericType;

      // Si es un tipo numérico declarado o una columna que parece numérica, validar los valores
      const colIndex = manifest.schema.columns.findIndex(c => c.name === col.name);

      const columnData = result.data_typed_array.map(row => {
        const value = row.values[colIndex];
        const isNull = value?.num === undefined && value?.str === undefined;
        const numValue = value?.num !== undefined ? Number(value.num) : value?.str !== undefined ? Number(value.str) : NaN;

        // Round-trip check: number -> string matches original (para detectar columnas de texto que son números)
        const roundTripMatch = isNull || (value?.str !== undefined ? String(Number(value.str)) === value.str : value?.num !== undefined);

        return { numValue, roundTripMatch, isNull };
      });

      const nonNullData = columnData.filter(d => !d.isNull);
      const allNoNaN = nonNullData.every(d => !isNaN(d.numValue));
      const allRoundTrip = columnData.every(d => d.roundTripMatch);
      const someNonZero = nonNullData.some(d => d.numValue !== 0);

      // Una columna es numérica si:
      // 1. Es de tipo numérico o todos sus valores pasan el chequeo de "ida y vuelta" (roundTripMatch)
      // 2. Y todos los valores no nulos son números válidos
      // 3. Y al menos algún valor debe ser distinto de cero.
      return (isNumericType || allRoundTrip) && allNoNaN && someNonZero;
    })
    .map(col => col.name);
};

export const DataBricksAiGenie_AnalyzeSimpleChartData_OLD = (
  $queryProcessResult: GenieStatementResponse
): DataBricksAiGenie_ChartAnalysisResult => {
  let queryProcessResult = _cloneDeep($queryProcessResult);

  // Validación inicial
  if (!queryProcessResult?.statement_response?.manifest?.schema || !queryProcessResult?.statement_response?.result) {
    return {
      isVisualizationAvailable: false,
      error: {
        code: 'INVALID_RESPONSE',
        message: 'Respuesta inválida o incompleta',
        details: { queryProcessResult },
      },
    };
  }

  const { manifest, result } = queryProcessResult.statement_response;
  const columns = manifest.schema.columns;

  // Verificar si hay exactamente 2 columnas
  if (columns.length !== 2) {
    return {
      isVisualizationAvailable: false,
      error: {
        code: 'INVALID_COLUMN_COUNT',
        message: `Se esperaban 2 columnas, pero se encontraron ${columns.length}`,
        details: { columns },
      },
    };
  }

  // Encontrar la columna numérica usando la función mejorada
  const numericColumns = getNumericColumns(queryProcessResult);
  let numericColumn = columns.find(col => numericColumns.includes(col.name));
  let labelColumn = columns.find(col => !numericColumns.includes(col.name));

  // Caso especial donde hay solo dos columnas pero las dos son numericas
  if (true && labelColumn == null && numericColumns.length === 2) {
    labelColumn = columns[0];
    numericColumn = columns[1];
  }

  if (!numericColumn || !labelColumn) {
    return {
      isVisualizationAvailable: false,
      error: {
        code: 'INVALID_COLUMN_TYPES',
        message: 'No se encontró una columna numérica válida y una columna de etiquetas',
        details: {
          numericColumns,
          availableColumns: columns.map(c => ({ name: c.name, type: c.type_text })),
          data: result.data_typed_array.slice(0, 3), // Muestra primeros 3 registros
        },
      },
    };
  }

  // Transformar los datos en formato de serie
  let seriesData = result.data_typed_array
    .map(row => {
      const numericValue = row.values[columns.indexOf(numericColumn)];
      const labelValue = row.values[columns.indexOf(labelColumn)];

      let label = labelValue?.str || '';
      // Asegurarnos de convertir el string a número si es necesario
      let valueNumber = numericValue?.num != null ? Number(numericValue.num) : numericValue?.str != null ? Number(numericValue.str) : null;

      return {
        label,
        value: valueNumber,
      };
    })
    .filter(item => item.value != null && !isNaN(item.value) && item.label !== '');

  // Determinar el tipo de gráfico más apropiado
  const chartType = determineChartType(seriesData);

  // Ordenar por valor de mayor a menor
  if (chartType === 'time_line') {
    // Agrupar por fecha y mantener el valor más alto para cada fecha
    const dateMap = new Map<string, number>();
    seriesData.forEach(item => {
      const dateKey = new Date(item.label).toISOString().split('T')[0]; // Normalizar fecha a YYYY-MM-DD

      if (false) {
        const currentValue = dateMap.get(dateKey) || 0;
        dateMap.set(dateKey, Math.max(currentValue, item.value));
      } else {
        const currentSum = dateMap.get(dateKey) || 0;
        dateMap.set(dateKey, currentSum + item.value); // Sumar valores del mismo día
      }
    });

    // Reconstruir seriesData con valores únicos por fecha
    seriesData.length = 0; // Limpiar array original
    Array.from(dateMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0])) // Ordenar por fecha
      .forEach(([dateStr, value]) => {
        seriesData.push({
          label: dateStr,
          value: value,
        });
      });
  } else {
    seriesData = seriesData.sort((a, b) => Number(b.value) - Number(a.value));
  }

  // Solo setear como graficable si hay datos válidos
  if (seriesData.length > 0) {
    const MAX_VISIBLE_RECORDS = 24;

    // Si hay más registros de los permitidos, tomar los primeros N
    if (chartType !== 'time_line' && seriesData.length > MAX_VISIBLE_RECORDS) {
      const originalLength = seriesData.length;
      seriesData = seriesData.slice(0, MAX_VISIBLE_RECORDS);

      // Agregar un elemento "Otros" con la suma del resto
      const remainingSum = result.data_typed_array
        .map(row => {
          const numericValue = row.values[columns.indexOf(numericColumn)];
          return numericValue?.num != null || numericValue?.str != null ? Number(numericValue?.num || numericValue?.str) : 0;
        })
        .sort((a, b) => b - a)
        .slice(MAX_VISIBLE_RECORDS)
        .reduce((sum, value) => sum + value, 0);

      if (remainingSum > 0) {
        seriesData.push({
          label: `Otros (${originalLength - MAX_VISIBLE_RECORDS} registros.)`,
          value: remainingSum,
        });
      }
    }

    return {
      isVisualizationAvailable: true,
      visualization: {
        series: seriesData,
        numericColumnName: numericColumn.name,
        labelColumnName: labelColumn.name,
        chartType: chartType,
      },
    };
  }

  return {
    isVisualizationAvailable: false,
    error: {
      code: 'NO_VALID_DATA',
      message: 'No se encontraron datos válidos para visualizar',
      details: {
        numericColumn,
        labelColumn,
        sampleData: result.data_typed_array.slice(0, 3),
      },
    },
  };
};

export const DataBricksAiGenie_AnalyzeSimpleChartData = (
  $queryProcessResult: GenieStatementResponse
): DataBricksAiGenie_ChartAnalysisResult => {
  let queryProcessResult = _cloneDeep($queryProcessResult);

  // Validación inicial
  if (!queryProcessResult?.statement_response?.manifest?.schema || !queryProcessResult?.statement_response?.result) {
    return {
      isVisualizationAvailable: false,
      error: {
        code: 'INVALID_RESPONSE',
        message: 'Respuesta inválida o incompleta',
        details: { queryProcessResult },
      },
    };
  }

  const { manifest, result } = queryProcessResult.statement_response;
  const columns = manifest.schema.columns;

  // Verificar si hay al menos 2 columnas
  if (columns.length < 2) {
    return {
      isVisualizationAvailable: false,
      error: {
        code: 'INVALID_COLUMN_COUNT',
        message: `Se esperaban al menos 2 columnas, pero se encontraron ${columns.length}`,
        details: { columns },
      },
    };
  }

  // Encontrar la columna numérica usando la función mejorada
  const numericColumns = getNumericColumns(queryProcessResult);
  let numericColumn = columns.find(col => numericColumns.includes(col.name));
  let labelColumn = columns.find(col => !numericColumns.includes(col.name));

  // Caso especial: si no hay columnas de etiqueta claras (todas parecen numéricas),
  // usamos la primera como etiqueta y la segunda como valor.
  if (labelColumn == null && numericColumns.length >= 2) {
    labelColumn = columns[0];
    numericColumn = columns[1];
  }

  if (!numericColumn || !labelColumn) {
    return {
      isVisualizationAvailable: false,
      error: {
        code: 'INVALID_COLUMN_TYPES',
        message: 'No se encontró una columna numérica válida y una columna de etiquetas',
        details: {
          numericColumns,
          availableColumns: columns.map(c => ({ name: c.name, type: c.type_text })),
          data: result.data_typed_array.slice(0, 3), // Muestra primeros 3 registros
        },
      },
    };
  }

  // Transformar los datos en formato de serie
  let seriesData = result.data_typed_array
    .map(row => {
      const numericValue = row.values[columns.indexOf(numericColumn)];
      const labelValue = row.values[columns.indexOf(labelColumn)];

      let label = labelValue?.str || '';
      // Asegurarnos de convertir el string a número si es necesario
      let valueNumber = numericValue?.num != null ? Number(numericValue.num) : numericValue?.str != null ? Number(numericValue.str) : null;

      return {
        label,
        value: valueNumber,
      };
    })
    .filter(item => item.value != null && !isNaN(item.value) && item.label !== '');

  // Determinar el tipo de gráfico más apropiado
  const chartType = determineChartType(seriesData);

  // Ordenar por valor de mayor a menor
  if (chartType === 'time_line') {
    // Agrupar por fecha y mantener el valor más alto para cada fecha
    const dateMap = new Map<string, number>();

    seriesData.forEach(item => {
      // Normalizar fecha a YYYY-MM-DD sin desplazamiento de zona horaria
      let dateKey = item.label;

      if (item.label.includes('T')) {
        dateKey = item.label.split('T')[0];
      } else if (item.label.includes(' ')) {
        dateKey = item.label.split(' ')[0];
      } else if (item.label.match(/^\d{13}$/)) {
        // En caso de timestamp de 13 dígitos
        dateKey = new Date(Number(item.label)).toISOString().split('T')[0];
      }

      const currentSum = dateMap.get(dateKey) || 0;
      dateMap.set(dateKey, currentSum + item.value); // Sumar valores del mismo día
    });

    // Reconstruir seriesData con valores únicos por fecha
    seriesData.length = 0; // Limpiar array original
    Array.from(dateMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0])) // Ordenar por fecha
      .forEach(([dateStr, value]) => {
        seriesData.push({
          label: dateStr,
          value: value,
        });
      });
  } else {
    seriesData = seriesData.sort((a, b) => Number(b.value) - Number(a.value));
  }

  // Solo setear como graficable si hay datos válidos
  if (seriesData.length > 0) {
    const MAX_VISIBLE_RECORDS = 24;

    // Si hay más registros de los permitidos, tomar los primeros N
    if (chartType !== 'time_line' && seriesData.length > MAX_VISIBLE_RECORDS) {
      const originalLength = seriesData.length;
      seriesData = seriesData.slice(0, MAX_VISIBLE_RECORDS);

      // Agregar un elemento "Otros" con la suma del resto
      const remainingSum = result.data_typed_array
        .map(row => {
          const numericValue = row.values[columns.indexOf(numericColumn)];
          return numericValue?.num != null || numericValue?.str != null ? Number(numericValue?.num || numericValue?.str) : 0;
        })
        .sort((a, b) => b - a)
        .slice(MAX_VISIBLE_RECORDS)
        .reduce((sum, value) => sum + value, 0);

      if (remainingSum > 0) {
        seriesData.push({
          label: `Otros (${originalLength - MAX_VISIBLE_RECORDS} registros.)`,
          value: remainingSum,
        });
      }
    }

    return {
      isVisualizationAvailable: true,
      visualization: {
        series: seriesData,
        numericColumnName: numericColumn.name,
        labelColumnName: labelColumn.name,
        chartType: chartType,
      },
    };
  }

  return {
    isVisualizationAvailable: false,
    error: {
      code: 'NO_VALID_DATA',
      message: 'No se encontraron datos válidos para visualizar',
      details: {
        numericColumn,
        labelColumn,
        sampleData: result.data_typed_array.slice(0, 3),
      },
    },
  };
};

/**
 * Analiza datos para multi-series (varias columnas numéricas vs una columna de etiqueta).
 *
 * Usado cuando el usuario selecciona >1 columna numérica en el ColumnSelectorDialog para
 * comparar dos métricas en el mismo gráfico (eventualmente con dual axis).
 *
 * `series` se setea con la primera columna numérica (compat con consumidores single-serie),
 * y `multiSeries` contiene una entrada por columna numérica seleccionada.
 *
 * `useDualAxis` se calcula solo para 2 series: true si las escalas (max abs) difieren más de 10x.
 */
export const DataBricksAiGenie_AnalyzeMultiSeriesChartData = (
  $queryProcessResult: GenieStatementResponse,
  numericColumnNames: string[],
  labelColumnName: string
): DataBricksAiGenie_ChartAnalysisResult => {
  try {
    return _multiSeriesAnalyzerImpl($queryProcessResult, numericColumnNames, labelColumnName);
  } catch (e) {
    return {
      isVisualizationAvailable: false,
      error: { code: 'UNEXPECTED', message: 'Error inesperado al analizar multi-serie', details: { error: String(e) } },
    };
  }
};

const _multiSeriesAnalyzerImpl = (
  $queryProcessResult: GenieStatementResponse,
  numericColumnNames: string[],
  labelColumnName: string
): DataBricksAiGenie_ChartAnalysisResult => {
  const queryProcessResult = _cloneDeep($queryProcessResult);

  if (!queryProcessResult?.statement_response?.manifest?.schema || !queryProcessResult?.statement_response?.result) {
    return {
      isVisualizationAvailable: false,
      error: { code: 'INVALID_RESPONSE', message: 'Respuesta inválida o incompleta' },
    };
  }

  if (!numericColumnNames?.length || !labelColumnName) {
    return {
      isVisualizationAvailable: false,
      error: { code: 'MISSING_COLUMNS', message: 'Faltan columnas numéricas o de etiqueta' },
    };
  }

  const { manifest, result } = queryProcessResult.statement_response;
  const columns = manifest.schema.columns;

  const labelIdx = columns.findIndex(c => c.name === labelColumnName);
  const numericIndexes = numericColumnNames.map(name => ({ name, idx: columns.findIndex(c => c.name === name) }));

  if (labelIdx === -1 || numericIndexes.some(n => n.idx === -1)) {
    return {
      isVisualizationAvailable: false,
      error: { code: 'INVALID_COLUMN_TYPES', message: 'Alguna columna no se encontró en el schema' },
    };
  }

  const rawLabels: string[] = result.data_typed_array.map(row => row.values[labelIdx]?.str ?? '');

  const validLabels = rawLabels.filter(l => l !== '');
  const areLabelsDate = validLabels.length > 0 && validLabels.every(l => isDateString(l));

  // Construir datos por serie (alineados por índice de fila)
  const seriesByName = numericIndexes.map(({ name, idx }) => {
    const data = result.data_typed_array.map((row, rowIdx) => {
      const v = row.values[idx];
      const label = rawLabels[rowIdx];
      const numValue = v?.num != null ? Number(v.num) : v?.str != null ? Number(v.str) : null;
      return { label, value: numValue };
    });
    return { name, data };
  });

  // Filtrar filas inválidas: label vacío o todas las series sin número válido
  const validRowIdx: number[] = [];
  for (let i = 0; i < rawLabels.length; i++) {
    const label = rawLabels[i];
    if (label === '') continue;
    const allInvalid = seriesByName.every(s => s.data[i].value == null || isNaN(s.data[i].value));
    if (allInvalid) continue;
    validRowIdx.push(i);
  }

  let cleanSeries = seriesByName.map(s => ({
    name: s.name,
    data: validRowIdx.map(i => ({
      label: s.data[i].label,
      value: s.data[i].value != null && !isNaN(s.data[i].value) ? s.data[i].value : 0,
    })),
  }));

  if (cleanSeries.length === 0 || cleanSeries[0].data.length === 0) {
    return {
      isVisualizationAvailable: false,
      error: { code: 'NO_VALID_DATA', message: 'No se encontraron datos válidos para visualizar' },
    };
  }

  let chartType: DataBricksAiGenie_ChartAnalysisResult_ChartType;

  if (areLabelsDate) {
    // Agrupar/sumar por fecha por cada serie, mantener alineado por fecha
    const dateSet = new Set<string>();
    const perSeriesMap: Map<string, number>[] = cleanSeries.map(() => new Map<string, number>());

    cleanSeries.forEach((s, sIdx) => {
      s.data.forEach(item => {
        let dateKey = item.label;
        if (item.label.includes('T')) dateKey = item.label.split('T')[0];
        else if (item.label.includes(' ')) dateKey = item.label.split(' ')[0];
        else if (item.label.match(/^\d{13}$/)) dateKey = new Date(Number(item.label)).toISOString().split('T')[0];

        dateSet.add(dateKey);
        perSeriesMap[sIdx].set(dateKey, (perSeriesMap[sIdx].get(dateKey) || 0) + (item.value || 0));
      });
    });

    const sortedDates = Array.from(dateSet).sort((a, b) => a.localeCompare(b));

    cleanSeries = cleanSeries.map((s, sIdx) => ({
      name: s.name,
      data: sortedDates.map(d => ({ label: d, value: perSeriesMap[sIdx].get(d) ?? 0 })),
    }));

    chartType = 'time_line';
  } else {
    // Ordenar por valor de la PRIMERA serie (descendente) y recortar a MAX
    const MAX_VISIBLE_RECORDS = 24;
    const labelsOrdered = [...cleanSeries[0].data]
      .map((item, idx) => ({ label: item.label, sortKey: item.value, originalIdx: idx }))
      .sort((a, b) => Number(b.sortKey) - Number(a.sortKey))
      .slice(0, MAX_VISIBLE_RECORDS)
      .map(x => ({ label: x.label, idx: x.originalIdx }));

    cleanSeries = cleanSeries.map(s => ({
      name: s.name,
      data: labelsOrdered.map(({ idx, label }) => ({ label, value: s.data[idx]?.value ?? 0 })),
    }));

    const recordCount = cleanSeries[0].data.length;
    const maxLabelLength = Math.max(...cleanSeries[0].data.map(item => (item.label || '').length));
    chartType = maxLabelLength > 15 || recordCount > 12 ? 'horizontalBar' : 'bar';
  }

  // Dual axis automático solo cuando son exactamente 2 series y las escalas difieren >10x
  let useDualAxis = false;
  if (cleanSeries.length === 2) {
    const maxAbs = (data: Array<{ value: number }>) => data.reduce((m, d) => Math.max(m, Math.abs(d.value ?? 0)), 0);
    const m1 = maxAbs(cleanSeries[0].data);
    const m2 = maxAbs(cleanSeries[1].data);
    if (m1 > 0 && m2 > 0) {
      const ratio = Math.max(m1, m2) / Math.min(m1, m2);
      useDualAxis = ratio > 10;
    }
  }

  return {
    isVisualizationAvailable: true,
    visualization: {
      // `series` mantiene compat con consumidores single-serie (primera serie)
      series: cleanSeries[0].data,
      numericColumnName: cleanSeries[0].name,
      labelColumnName,
      chartType,
      multiSeries: cleanSeries,
      numericColumnNames: cleanSeries.map(s => s.name),
      useDualAxis,
    },
  };
};

export const _formatColumnTitle = (columnName: string): string => {
  return (
    columnName
      // Separar por underscore y otros caracteres especiales
      .split(/[_\s]+/)
      // Capitalizar primera letra de cada palabra
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      // Unir con espacios
      .join(' ')
  );
};
